import {
  adminV2WorkspaceActionSlotBindingSchema,
  assertAdminV2WorkspaceActionBindings,
} from '@/admin-v2/workspace/workspace-action-slots';
import { createAdminV2WorkspaceInputLabelsSchema } from '@/admin-v2/workspace/workspace-input-labels';
import { adminV2WorkspaceToolbarSchema } from '@/admin-v2/workspace/workspace-toolbar';
import {
  CLIENT_IMAGE_META_DIRECT_TRUST_FIELDS,
  CLIENT_IMAGE_META_FIELDS,
  isClientImageMetaDirectTrustField,
} from '@/ai/image/client-meta';
import { z } from 'zod';
import {
  adminV2AllowedImageMimeTypeSchema,
  adminV2ImageColorModeSchema,
} from '../image-constraint-options';
import { adminV2ProviderCostPolicySchema } from '../models/provider-cost-policy';
import {
  adminV2LocalizedTextSchema,
  adminV2TranslationMetadataSchema,
} from '../translation/translation-contract';
import { getRequiredAdminV2ClientImageMetaFields } from './image-constraint-client-meta';
import type { AdminV2MediaMetaSource } from './media-meta-context';
import {
  adminV2ParameterCalculatorInstancesSchema,
  validateAdminV2ParameterCalculatorInstance,
} from './parameter-calculator';
import {
  adminV2ToolFieldControls,
  getAdminV2ToolFieldControlCapability,
  getAdminV2ToolFieldDefaultValueKind,
  supportsAdminV2ToolFieldDefaultValue,
  supportsAdminV2ToolFieldItemConstraints,
  supportsAdminV2ToolFieldOptions,
  supportsAdminV2ToolFieldPlaceholder,
} from './tool-field-control-capabilities';
import {
  isAdminV2UserFacingToolField,
  isAdminV2UserFacingToolOptionValue,
} from './tool-field-visibility';
import { adminV2ToolIconSchema } from './tool-icon';
import {
  isAdminV2ComputedResolverKey,
  isAdminV2ComputedValueKey,
  isAdminV2MediaMetaResolverKey,
  isAdminV2MediaMetaTransformerKey,
  isAdminV2PreflightValidatorKey,
  isAdminV2RegisteredConditionKey,
  isAdminV2RegisteredCreditCalculatorKey,
  isAdminV2TemplateResolverKey,
} from './tool-runtime-registries';
import { isAdminV2ValidHttpUrl } from './tool-url';

const guardPolicyKeySchema = z.enum([
  'wavespeed-ai/content-moderator/text',
  'wavespeed-ai/content-moderator/image',
  'imgenhancer/content-moderator/video',
]);

export const DEFAULT_ADMIN_V2_TOOL_FALLBACK_ERROR_CODES = [
  'ADMIN_V2_PROVIDER_FAILED',
  'ADMIN_V2_PROVIDER_TIMEOUT',
  'ADMIN_V2_PROVIDER_RATE_LIMITED',
  'ADMIN_V2_MODEL_UNAVAILABLE',
  'ADMIN_V2_PROVIDER_RESPONSE_INVALID',
  'ADMIN_V2_MODEL_OUTPUT_INVALID',
  'ADMIN_V2_MODEL_OUTPUT_LIMIT_EXCEEDED',
] as const;

const fallbackErrorCodeSchema = z.enum([
  ...DEFAULT_ADMIN_V2_TOOL_FALLBACK_ERROR_CODES,
  'CONTENT_POLICY_BLOCKED',
]);

const clientImageMetaRequirementsSchema = z
  .object({
    fields: z.array(z.enum(CLIENT_IMAGE_META_FIELDS)).min(1),
  })
  .strict();

const workspaceImageInputConstraintRuleSchema = z
  .object({
    activeWhen: z
      .object({
        fieldId: z.string().min(1),
        values: z.array(z.union([z.string(), z.number(), z.boolean()])).min(1),
      })
      .strict(),
    modelUsageIds: z.array(z.string().min(1)).min(1),
  })
  .strict();

function validateUniqueToolStringArray(
  values: readonly string[],
  context: z.RefinementCtx,
  message: string
) {
  const seenValues = new Set<string>();
  values.forEach((value, index) => {
    if (seenValues.has(value)) {
      context.addIssue({
        code: 'custom',
        message,
        path: [index],
      });
    }
    seenValues.add(value);
  });
}

const toolImageAllowedMimeTypesSchema = z
  .array(adminV2AllowedImageMimeTypeSchema)
  .superRefine((values, context) =>
    validateUniqueToolStringArray(
      values,
      context,
      'Allowed MIME types must be unique.'
    )
  );
const toolImageColorModesSchema = z
  .array(adminV2ImageColorModeSchema)
  .superRefine((values, context) =>
    validateUniqueToolStringArray(
      values,
      context,
      'Color modes must be unique.'
    )
  );

export const adminV2FreeAccessPolicyRule = {
  requiredAccess: 'free' as const,
  planIds: [] as string[],
  roleIds: [] as string[],
};

export const accessPolicyRuleSchema = z
  .object({
    alwaysEnforced: z.boolean().optional(),
    requiredAccess: z.enum(['free', 'authenticated', 'paid', 'plan', 'role']),
    planIds: z.array(z.string().min(1)),
    roleIds: z.array(z.string().min(1)),
  })
  .strict()
  .superRefine((rule, context) => {
    if (rule.requiredAccess === 'plan' && rule.planIds.length === 0) {
      context.addIssue({
        code: 'custom',
        message: 'Plan access policies must include at least one plan id.',
        path: ['planIds'],
      });
    }
    if (rule.requiredAccess === 'role' && rule.roleIds.length === 0) {
      context.addIssue({
        code: 'custom',
        message: 'Role access policies must include at least one role id.',
        path: ['roleIds'],
      });
    }
  });

const toolFieldControlSchema = z.enum(adminV2ToolFieldControls);

const helpPreviewUrlSchema = z.union([
  z
    .string()
    .url()
    .regex(/^https:\/\//),
  z
    .string()
    .regex(
      /^\/(?:workspace\/)?tools\/(?:[a-z0-9_-]+\/)*[a-z0-9_-]+\.(?:png|webp|gif)$/i
    ),
]);

const helpPreviewSchema = z
  .object({
    imageUrl: helpPreviewUrlSchema.optional(),
    posterUrl: helpPreviewUrlSchema.optional(),
    comparisonLabels: z
      .object({
        before: z.string().trim().min(1),
        after: z.string().trim().min(1),
      })
      .strict()
      .optional(),
  })
  .strict();

export const adminV2ToolFieldSchema = z
  .object({
    id: z.string().min(1),
    label: adminV2LocalizedTextSchema,
    placeholder: adminV2LocalizedTextSchema.optional(),
    tooltip: adminV2LocalizedTextSchema.optional(),
    helpPreview: helpPreviewSchema.optional(),
    control: toolFieldControlSchema,
    workspaceGroup: z.enum(['main', 'advanced']).optional(),
    workspaceControlVariant: z
      .enum(['segmented-slider', 'stepped-slider', 'tabs', 'upscale-size'])
      .optional(),
    workspaceSliderSnapPoints: z.array(z.number()).min(1).optional(),
    required: z.boolean(),
    defaultValue: z.unknown().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().positive().optional(),
    minLength: z.number().int().min(0).optional(),
    maxLength: z.number().int().min(1).optional(),
    minItems: z.number().int().min(0).optional(),
    maxItems: z.number().int().min(1).optional(),
    accessPolicy: accessPolicyRuleSchema,
    allowBatch: z.boolean(),
    inputRole: z.enum([
      'parameter',
      'primaryImage',
      'referenceImage',
      'firstFrame',
      'lastFrame',
      'referenceVideo',
      'referenceAudio',
      'prompt',
      'internal',
    ]),
    prefillable: z.boolean(),
    adminOnly: z.boolean(),
    userVisible: z.boolean(),
    activeWhen: z
      .object({
        fieldId: z.string().min(1),
        values: z.array(z.union([z.string(), z.number(), z.boolean()])).min(1),
      })
      .strict()
      .optional(),
    imageConstraints: z
      .object({
        allowedMimeTypes: toolImageAllowedMimeTypesSchema,
        maxCount: z.number().int().min(1).optional(),
        maxSingleBytes: z.number().int().min(1).optional(),
        maxTotalBytes: z.number().int().min(1).optional(),
        requireKnownContentLength: z.boolean(),
        minWidth: z.number().int().min(1).optional(),
        minHeight: z.number().int().min(1).optional(),
        maxWidth: z.number().int().min(1).optional(),
        maxHeight: z.number().int().min(1).optional(),
        maxMegapixels: z.number().positive().optional(),
        colorModes: toolImageColorModesSchema,
      })
      .strict()
      .optional(),
    mediaConstraints: z
      .object({
        allowedMimeTypes: z.array(
          z.string().regex(/^[a-z0-9.+-]+\/[a-z0-9.+-]+$/i)
        ),
        maxCount: z.number().int().min(1).optional(),
        maxSingleBytes: z.number().int().min(1).optional(),
        maxTotalBytes: z.number().int().min(1).optional(),
        minSingleDurationSeconds: z.number().positive().optional(),
        maxSingleDurationSeconds: z.number().positive().optional(),
        maxTotalDurationSeconds: z.number().positive().optional(),
        minVideoShortEdge: z.number().int().positive().optional(),
        maxVideoShortEdge: z.number().int().positive().optional(),
      })
      .strict()
      .optional(),
    clientImageMetaRequirements: clientImageMetaRequirementsSchema.optional(),
    options: z.array(
      z
        .object({
          label: adminV2LocalizedTextSchema,
          labelOverride: z
            .object({
              fieldId: z.string().min(1),
              value: z.union([z.string(), z.number(), z.boolean()]),
              label: adminV2LocalizedTextSchema,
            })
            .strict()
            .optional(),
          description: adminV2LocalizedTextSchema.optional(),
          helpPreview: helpPreviewSchema.optional(),
          icon: z.string().min(1).optional(),
          value: z.union([z.string(), z.number(), z.boolean()]),
          accessPolicy: accessPolicyRuleSchema,
          hidden: z.boolean(),
          disabled: z.boolean(),
          sortOrder: z.number().int().min(0).optional(),
        })
        .strict()
    ),
  })
  .strict();

type AdminV2ToolFieldForValidation = z.infer<typeof adminV2ToolFieldSchema>;
type AdminV2ToolFieldOptionValue =
  AdminV2ToolFieldForValidation['options'][number]['value'];

function getOptionValueKey(value: AdminV2ToolFieldOptionValue) {
  return `${typeof value}:${String(value)}`;
}

function hasMatchingOptionValue(
  options: AdminV2ToolFieldForValidation['options'],
  value: unknown
) {
  return options.some((option) => Object.is(option.value, value));
}

function addToolFieldDefaultIssue(params: {
  context: z.RefinementCtx;
  fieldIndex: number;
  message: string;
}) {
  params.context.addIssue({
    code: 'custom',
    message: params.message,
    path: ['toolFields', params.fieldIndex, 'defaultValue'],
  });
}

function validateToolFieldDefaultValue(params: {
  context: z.RefinementCtx;
  field: AdminV2ToolFieldForValidation;
  fieldIndex: number;
}) {
  const { context, field, fieldIndex } = params;
  const defaultValue = field.defaultValue;
  if (defaultValue === undefined) {
    return;
  }

  if (!supportsAdminV2ToolFieldDefaultValue(field.control)) {
    addToolFieldDefaultIssue({
      context,
      fieldIndex,
      message: 'Upload fields cannot define workspace defaultValue.',
    });
    return;
  }

  const defaultValueKind = getAdminV2ToolFieldDefaultValueKind(field.control);

  if (defaultValueKind === 'option') {
    if (!hasMatchingOptionValue(field.options, defaultValue)) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message: 'Select field defaultValue must match a configured option.',
      });
      return;
    }
    if (
      isAdminV2UserFacingToolField(field) &&
      !isAdminV2UserFacingToolOptionValue(field, defaultValue)
    ) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message: 'Select field defaultValue must match a user-visible option.',
      });
    }
    return;
  }

  if (defaultValueKind === 'optionArray') {
    if (!Array.isArray(defaultValue)) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message: 'Multi-select field defaultValue must be an array.',
      });
      return;
    }
    if (
      defaultValue.some(
        (value) => !hasMatchingOptionValue(field.options, value)
      )
    ) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message:
          'Multi-select field defaultValue items must match configured options.',
      });
      return;
    }
    if (
      isAdminV2UserFacingToolField(field) &&
      defaultValue.some(
        (value) => !isAdminV2UserFacingToolOptionValue(field, value)
      )
    ) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message:
          'Multi-select field defaultValue items must match user-visible options.',
      });
      return;
    }
    if (field.minItems !== undefined && defaultValue.length < field.minItems) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message:
          'Multi-select field defaultValue cannot have fewer items than minItems.',
      });
    }
    if (field.maxItems !== undefined && defaultValue.length > field.maxItems) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message:
          'Multi-select field defaultValue cannot have more items than maxItems.',
      });
    }
    return;
  }

  if (defaultValueKind === 'number') {
    if (typeof defaultValue !== 'number' || !Number.isFinite(defaultValue)) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message: 'Numeric field defaultValue must be a finite number.',
      });
      return;
    }
    if (field.min !== undefined && defaultValue < field.min) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message: 'Numeric field defaultValue cannot be less than min.',
      });
    }
    if (field.max !== undefined && defaultValue > field.max) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message: 'Numeric field defaultValue cannot be greater than max.',
      });
    }
    return;
  }

  if (defaultValueKind === 'boolean') {
    if (typeof defaultValue !== 'boolean') {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message: 'Toggle field defaultValue must be boolean.',
      });
    }
    return;
  }

  if (defaultValueKind === 'array') {
    if (!Array.isArray(defaultValue)) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message: 'Array-like field defaultValue must be an array.',
      });
      return;
    }
    if (field.minItems !== undefined && defaultValue.length < field.minItems) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message:
          'Array-like field defaultValue cannot have fewer items than minItems.',
      });
    }
    if (field.maxItems !== undefined && defaultValue.length > field.maxItems) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message:
          'Array-like field defaultValue cannot have more items than maxItems.',
      });
    }
    return;
  }

  if (defaultValueKind === 'string') {
    if (typeof defaultValue !== 'string') {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message: 'Text-like field defaultValue must be a string.',
      });
      return;
    }
    if (field.control === 'url' && !isAdminV2ValidHttpUrl(defaultValue)) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message: 'URL field defaultValue must be a valid URL.',
      });
    }
    if (
      field.minLength !== undefined &&
      defaultValue.length < field.minLength
    ) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message:
          'Text-like field defaultValue cannot be shorter than minLength.',
      });
    }
    if (
      field.maxLength !== undefined &&
      defaultValue.length > field.maxLength
    ) {
      addToolFieldDefaultIssue({
        context,
        fieldIndex,
        message: 'Text-like field defaultValue cannot exceed maxLength.',
      });
    }
  }
}

function addToolFieldCapabilityIssue(params: {
  context: z.RefinementCtx;
  fieldIndex: number;
  field: string;
  message: string;
}) {
  params.context.addIssue({
    code: 'custom',
    message: params.message,
    path: ['toolFields', params.fieldIndex, params.field],
  });
}

function validateToolFieldControlCapabilities(params: {
  context: z.RefinementCtx;
  field: AdminV2ToolFieldForValidation;
  fieldIndex: number;
}) {
  const { context, field, fieldIndex } = params;
  const capability = getAdminV2ToolFieldControlCapability(field.control);
  const inputRole = field.inputRole ?? 'parameter';

  if (
    field.placeholder &&
    !supportsAdminV2ToolFieldPlaceholder({
      control: field.control,
      inputRole,
    })
  ) {
    addToolFieldCapabilityIssue({
      context,
      fieldIndex,
      field: 'placeholder',
      message: 'This Tool field control does not render placeholder text.',
    });
  }

  if (!capability.allowsNumericConstraints) {
    if (field.min !== undefined) {
      addToolFieldCapabilityIssue({
        context,
        fieldIndex,
        field: 'min',
        message: 'Only numeric Tool field controls can define min.',
      });
    }
    if (field.max !== undefined) {
      addToolFieldCapabilityIssue({
        context,
        fieldIndex,
        field: 'max',
        message: 'Only numeric Tool field controls can define max.',
      });
    }
    if (field.step !== undefined) {
      addToolFieldCapabilityIssue({
        context,
        fieldIndex,
        field: 'step',
        message: 'Only numeric Tool field controls can define step.',
      });
    }
  }

  if (!capability.allowsLengthConstraints) {
    if (field.minLength !== undefined) {
      addToolFieldCapabilityIssue({
        context,
        fieldIndex,
        field: 'minLength',
        message: 'Only text-like Tool field controls can define minLength.',
      });
    }
    if (field.maxLength !== undefined) {
      addToolFieldCapabilityIssue({
        context,
        fieldIndex,
        field: 'maxLength',
        message: 'Only text-like Tool field controls can define maxLength.',
      });
    }
  }

  if (
    !supportsAdminV2ToolFieldItemConstraints({
      control: field.control,
      allowBatch: field.allowBatch,
    })
  ) {
    if (field.minItems !== undefined) {
      addToolFieldCapabilityIssue({
        context,
        fieldIndex,
        field: 'minItems',
        message:
          'Only array-like or batched upload Tool field controls can define minItems.',
      });
    }
    if (field.maxItems !== undefined) {
      addToolFieldCapabilityIssue({
        context,
        fieldIndex,
        field: 'maxItems',
        message:
          'Only array-like or batched upload Tool field controls can define maxItems.',
      });
    }
  }

  if (field.allowBatch && !capability.allowsBatchUpload) {
    addToolFieldCapabilityIssue({
      context,
      fieldIndex,
      field: 'allowBatch',
      message: 'Only media upload fields can enable batch upload.',
    });
  }

  if (field.mediaConstraints && !capability.allowsMediaConstraints) {
    addToolFieldCapabilityIssue({
      context,
      fieldIndex,
      field: 'mediaConstraints',
      message: 'Only video or audio Tool fields can define media constraints.',
    });
  }

  if (field.imageConstraints && !capability.allowsImageConstraints) {
    addToolFieldCapabilityIssue({
      context,
      fieldIndex,
      field: 'imageConstraints',
      message: 'Only image Tool field controls can define image constraints.',
    });
  }

  if (
    field.clientImageMetaRequirements &&
    !capability.allowsClientImageMetaRequirements
  ) {
    addToolFieldCapabilityIssue({
      context,
      fieldIndex,
      field: 'clientImageMetaRequirements',
      message:
        'Only image Tool field controls can require client image metadata.',
    });
  }

  if (!capability.inputRoles.includes(inputRole)) {
    addToolFieldCapabilityIssue({
      context,
      fieldIndex,
      field: 'inputRole',
      message: `Tool field control ${field.control} cannot use inputRole ${inputRole}.`,
    });
  }

  if (!supportsAdminV2ToolFieldOptions(field.control) && field.options.length) {
    addToolFieldCapabilityIssue({
      context,
      fieldIndex,
      field: 'options',
      message: 'Only selector Tool field controls can define options.',
    });
  }
  if (field.workspaceControlVariant && field.control !== 'select') {
    addToolFieldCapabilityIssue({
      context,
      fieldIndex,
      field: 'workspaceControlVariant',
      message: 'Workspace selection presentation requires a select Tool field.',
    });
  }
  if (field.workspaceSliderSnapPoints && field.control !== 'slider') {
    addToolFieldCapabilityIssue({
      context,
      fieldIndex,
      field: 'workspaceSliderSnapPoints',
      message: 'Workspace slider snap points require a slider Tool field.',
    });
  }
}

function validateAdminV2ToolFieldList(
  fields: AdminV2ToolFieldForValidation[],
  context: z.RefinementCtx
) {
  const fieldIds = new Set<string>();
  fields.forEach((field, index) => {
    if (fieldIds.has(field.id)) {
      context.addIssue({
        code: 'custom',
        message: 'Tool field ids must be stable and unique.',
        path: ['toolFields', index, 'id'],
      });
    }
    const options = field.options ?? [];
    validateToolFieldControlCapabilities({
      context,
      field,
      fieldIndex: index,
    });
    if (field.adminOnly && field.userVisible) {
      context.addIssue({
        code: 'custom',
        message: 'Admin-only fields cannot be exposed to users.',
        path: ['toolFields', index, 'userVisible'],
      });
    }
    if (field.inputRole === 'internal') {
      if (field.adminOnly !== true) {
        context.addIssue({
          code: 'custom',
          message: 'Internal fields must be admin-only.',
          path: ['toolFields', index, 'adminOnly'],
        });
      }
      if (field.userVisible !== false) {
        context.addIssue({
          code: 'custom',
          message: 'Internal fields cannot be exposed to users.',
          path: ['toolFields', index, 'userVisible'],
        });
      }
    }
    if (
      supportsAdminV2ToolFieldOptions(field.control) &&
      options.length === 0
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Selector fields must include at least one option.',
        path: ['toolFields', index, 'options'],
      });
    }
    const optionValueKeys = new Set<string>();
    options.forEach((option, optionIndex) => {
      const optionValueKey = getOptionValueKey(option.value);
      if (optionValueKeys.has(optionValueKey)) {
        context.addIssue({
          code: 'custom',
          message: 'Selector option values must be unique.',
          path: ['toolFields', index, 'options', optionIndex, 'value'],
        });
      }
      optionValueKeys.add(optionValueKey);
    });
    validateToolFieldDefaultValue({
      context,
      field,
      fieldIndex: index,
    });
    if (
      field.min !== undefined &&
      field.max !== undefined &&
      field.min > field.max
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Field min cannot be greater than max.',
        path: ['toolFields', index, 'min'],
      });
    }
    if (
      field.minLength !== undefined &&
      field.maxLength !== undefined &&
      field.minLength > field.maxLength
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Field minLength cannot be greater than maxLength.',
        path: ['toolFields', index, 'minLength'],
      });
    }
    if (
      field.minItems !== undefined &&
      field.maxItems !== undefined &&
      field.minItems > field.maxItems
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Field minItems cannot be greater than maxItems.',
        path: ['toolFields', index, 'minItems'],
      });
    }
    fieldIds.add(field.id);
  });

  for (const [index, field] of fields.entries()) {
    for (const [optionIndex, option] of field.options.entries()) {
      if (!option.labelOverride) continue;
      const owner = fields.find(
        (candidate) => candidate.id === option.labelOverride?.fieldId
      );
      if (
        !owner ||
        owner.id === field.id ||
        owner.control !== 'select' ||
        owner.activeWhen ||
        !owner.options.some((item) =>
          Object.is(item.value, option.labelOverride?.value)
        )
      ) {
        context.addIssue({
          code: 'custom',
          path: ['toolFields', index, 'options', optionIndex, 'labelOverride'],
          message:
            'Option label overrides must reference a value of another unconditional select field.',
        });
      }
    }
    if (!field.activeWhen) continue;
    const owner = fields.find(
      (candidate) => candidate.id === field.activeWhen?.fieldId
    );
    if (
      !owner ||
      owner.id === field.id ||
      owner.control !== 'select' ||
      owner.activeWhen ||
      field.activeWhen.values.some(
        (value) =>
          !owner.options.some((option) => Object.is(option.value, value))
      )
    ) {
      context.addIssue({
        code: 'custom',
        path: [index, 'activeWhen'],
        message:
          'Conditional fields must reference values of an unconditional select field.',
      });
    }
  }
  return fieldIds;
}

export const adminV2ToolFieldListSchema = z
  .array(adminV2ToolFieldSchema)
  .superRefine((fields, context) => {
    validateAdminV2ToolFieldList(fields, context);
  });

export function parseAdminV2ToolFieldList(input: unknown) {
  return adminV2ToolFieldListSchema.parse(input);
}

const parameterSourceSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('toolField'),
      fieldId: z.string().min(1),
      valueMap: z
        .array(
          z
            .object({
              from: z.union([z.string(), z.number(), z.boolean()]),
              to: z.unknown(),
            })
            .strict()
        )
        .optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('fixedValue'),
      value: z.unknown(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('computedValue'),
      key: z.string().min(1),
    })
    .strict(),
  z
    .object({
      kind: z.literal('computedResolver'),
      key: z.string().min(1),
    })
    .strict(),
  z
    .object({
      kind: z.literal('templateResolver'),
      key: z.string().min(1),
      template: z.string().min(1).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('mediaMetaResolver'),
      key: z.string().min(1),
      fieldId: z.string().min(1),
      metaSource: z.enum(['clientProvidedMeta', 'providerReturnedMeta']),
      metaPath: z.array(z.string().min(1)).min(1),
    })
    .strict(),
  z
    .object({
      kind: z.literal('previousOutput'),
      fromUsageId: z.string().min(1),
      outputKey: z.string().min(1),
      outputPath: z.array(z.string().min(1)).optional(),
    })
    .strict(),
]);

const primitiveConditionValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
]);

/**
 * Credit conditions are intentionally limited to values that Workspace can
 * derive locally from the published Tool contract and browser image metadata.
 * The server still evaluates the same policy before reserving credits.
 */
const clientCreditConditionSchema = z.discriminatedUnion('source', [
  z
    .object({
      source: z.literal('toolField'),
      fieldId: z.string().min(1),
      equals: primitiveConditionValueSchema,
    })
    .strict(),
  z
    .object({
      source: z.literal('clientImageMeta'),
      fieldId: z.string().min(1),
      metaPath: z.array(z.string().min(1)).min(1),
      equals: primitiveConditionValueSchema,
    })
    .strict(),
]);

type AdminV2ExecutionConditionInput = {
  fieldId?: string;
  mediaFieldId?: string;
  mediaMetaSource?: AdminV2MediaMetaSource;
  mediaMetaPath?: string[];
  previousOutput?: {
    fromUsageId: string;
    outputKey: string;
    outputPath?: string[];
  };
  providerErrorCode?: string;
  equals?: string | number | boolean;
  notEquals?: string | number | boolean;
  in?: Array<string | number | boolean>;
  notIn?: Array<string | number | boolean>;
  range?: {
    min?: number;
    max?: number;
  };
  exists?: boolean;
  empty?: boolean;
  registeredCondition?: string;
  and?: AdminV2ExecutionConditionInput[];
  or?: AdminV2ExecutionConditionInput[];
  not?: AdminV2ExecutionConditionInput;
  elseChildren?: string[];
};

const executionConditionSchema: z.ZodType<AdminV2ExecutionConditionInput> =
  z.lazy(() =>
    z
      .object({
        fieldId: z.string().min(1).optional(),
        mediaFieldId: z.string().min(1).optional(),
        mediaMetaSource: z
          .enum(['clientProvidedMeta', 'providerReturnedMeta'])
          .optional(),
        mediaMetaPath: z.array(z.string().min(1)).min(1).optional(),
        previousOutput: z
          .object({
            fromUsageId: z.string().min(1),
            outputKey: z.string().min(1),
            outputPath: z.array(z.string().min(1)).optional(),
          })
          .strict()
          .optional(),
        providerErrorCode: z.string().min(1).optional(),
        equals: primitiveConditionValueSchema.optional(),
        notEquals: primitiveConditionValueSchema.optional(),
        in: z.array(primitiveConditionValueSchema).min(1).optional(),
        notIn: z.array(primitiveConditionValueSchema).min(1).optional(),
        range: z
          .object({
            min: z.number().optional(),
            max: z.number().optional(),
          })
          .strict()
          .optional(),
        exists: z.boolean().optional(),
        empty: z.boolean().optional(),
        registeredCondition: z.string().min(1).optional(),
        and: z.array(executionConditionSchema).min(1).optional(),
        or: z.array(executionConditionSchema).min(1).optional(),
        not: executionConditionSchema.optional(),
        elseChildren: z.array(z.string().min(1)),
      })
      .strict()
  );

const executionNodeSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1).optional(),
    nodeType: z.enum(['runModel', 'sequence', 'branch', 'fallback', 'guard']),
    modelUsageId: z.string().min(1).optional(),
    guardPolicyKey: guardPolicyKeySchema.optional(),
    fallbackErrorCodes: z.array(fallbackErrorCodeSchema).optional(),
    children: z.array(z.string().min(1)),
    condition: executionConditionSchema.optional(),
    providerCostPolicyOverride: adminV2ProviderCostPolicySchema.optional(),
    preflightValidators: z
      .array(
        z
          .object({
            key: z.string().min(1),
            fieldId: z.string().min(1).optional(),
            errorCode: z.string().min(1).optional(),
          })
          .strict()
      )
      .optional(),
    mediaMetaTransformers: z
      .array(
        z
          .object({
            key: z.string().min(1),
            targetFieldId: z.string().min(1).optional(),
          })
          .strict()
      )
      .optional(),
  })
  .strict();

export const adminV2CreditFactorSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('constant'),
      value: z.number().int().min(0),
    })
    .strict(),
  z
    .object({
      kind: z.literal('toolField'),
      fieldId: z.string().min(1),
      defaultValue: z.number().int().min(0),
      values: z.array(
        z
          .object({
            value: z.union([z.string(), z.number(), z.boolean()]),
            credits: z.number().int().min(0),
          })
          .strict()
      ),
    })
    .strict(),
  z
    .object({
      kind: z.literal('conditional'),
      conditions: z.array(clientCreditConditionSchema).min(1),
      credits: z.number().int().min(0),
      defaultValue: z.number().int().min(0),
    })
    .strict(),
  z
    .object({
      kind: z.literal('inputCount'),
      fieldId: z.string().min(1),
      defaultValue: z.number().int().min(0),
    })
    .strict(),
  z
    .object({
      kind: z.literal('executionBranch'),
      defaultValue: z.number().int().min(0),
      branches: z.array(
        z
          .object({
            modelUsageId: z.string().min(1),
            credits: z.number().int().min(0),
          })
          .strict()
      ),
    })
    .strict(),
  z
    .object({
      kind: z.literal('outputCount'),
      outputKey: z.string().min(1).optional(),
      defaultValue: z.number().int().min(0),
    })
    .strict(),
  z
    .object({
      kind: z.literal('mediaMeta'),
      fieldId: z.string().min(1),
      metaSource: z.enum(['clientProvidedMeta', 'providerReturnedMeta']),
      metaPath: z.array(z.string().min(1)).min(1),
      defaultValue: z.number().int().min(0),
    })
    .strict(),
  z
    .object({
      kind: z.literal('nodeExecuted'),
      nodeId: z.string().min(1),
      credits: z.number().int().min(0),
      defaultValue: z.number().int().min(0),
    })
    .strict(),
  z
    .object({
      kind: z.literal('registeredCalculator'),
      key: z.string().min(1),
    })
    .strict(),
]);

export const adminV2CreditTermSchema = z
  .object({
    factors: z.array(adminV2CreditFactorSchema).min(1),
  })
  .strict();

export const adminV2PaidAccessDiscountSchema = z
  .object({
    percentOff: z.number().int().min(1).max(100),
    terms: z.array(adminV2CreditTermSchema).optional(),
  })
  .strict();

const parameterTargetBindingSchema = z
  .object({
    targetParameterId: z.string().min(1),
    sourcePath: z.array(z.string().min(1)).min(1),
    optional: z.boolean().optional(),
  })
  .strict();

const parameterMappingSchema = z
  .object({
    modelUsageId: z.string().min(1),
    targetParameterId: z.string().min(1).optional(),
    targetBindings: z.array(parameterTargetBindingSchema).min(1).optional(),
    source: parameterSourceSchema,
  })
  .strict()
  .superRefine((mapping, context) => {
    const targetCount =
      (mapping.targetParameterId ? 1 : 0) + (mapping.targetBindings ? 1 : 0);
    if (targetCount !== 1) {
      context.addIssue({
        code: 'custom',
        message:
          'Parameter mappings must use either targetParameterId or targetBindings.',
        path: ['targetParameterId'],
      });
    }
  });

const toolOutputTypeSchema = z.enum([
  'image',
  'imageList',
  'file',
  'text',
  'video',
]);

export const adminV2WorkspaceTemplatePreviewSchema = z
  .object({
    storageKey: z.string().trim().min(1),
    mimeType: z.literal('image/webp'),
    width: z.number().int().min(1),
    height: z.number().int().min(1),
  })
  .strict();

export const adminV2WorkspaceTemplateAnimatedPreviewSchema = z
  .object({
    storageKey: z.string().trim().min(1),
    mimeType: z.literal('image/gif'),
    width: z.number().int().min(1),
    height: z.number().int().min(1),
  })
  .strict();

export const adminV2WorkspaceTemplatePickerDraftSchema = z
  .object({
    basePrompt: z.string().trim().min(1),
    groups: z
      .array(
        z
          .object({
            id: z.string().trim().min(1),
            required: z.boolean(),
            selectionFieldId: z.string().trim().min(1),
            customImageFieldId: z.string().trim().min(1).optional(),
            customPrompt: z.string().trim().min(1).optional(),
            templates: z
              .array(
                z
                  .object({
                    value: z.string().trim().min(1),
                    preview: adminV2WorkspaceTemplatePreviewSchema,
                    animatedPreview:
                      adminV2WorkspaceTemplateAnimatedPreviewSchema.optional(),
                    prompt: z.string().trim().min(1),
                  })
                  .strict()
              )
              .min(1),
          })
          .strict()
      )
      .min(1),
  })
  .strict();

export type AdminV2WorkspaceTemplatePickerDraft = z.infer<
  typeof adminV2WorkspaceTemplatePickerDraftSchema
>;

export const adminV2WorkspaceRouteTemplatePickerDraftSchema = z
  .object({
    selectionFieldId: z.string().trim().min(1),
    templates: z
      .array(
        z
          .object({
            value: z.string().trim().min(1),
            preview: adminV2WorkspaceTemplatePreviewSchema,
          })
          .strict()
      )
      .min(1),
  })
  .strict();

export type AdminV2WorkspaceRouteTemplatePickerDraft = z.infer<
  typeof adminV2WorkspaceRouteTemplatePickerDraftSchema
>;

export const adminV2ToolDraftSchema = z
  .object({
    name: z.string().min(1),
    badge: z.string().trim().min(1).nullable().optional(),
    icon: adminV2ToolIconSchema.nullable().optional(),
    summary: z.string().trim().min(1).nullable().optional(),
    localizedDisplay: z
      .object({
        name: adminV2LocalizedTextSchema.optional(),
        badge: adminV2LocalizedTextSchema.optional(),
        summary: adminV2LocalizedTextSchema.optional(),
      })
      .strict()
      .optional(),
    workspaceEnabled: z.boolean(),
    runtimeType: z.enum(['aiJob', 'clientOnly']),
    availableSurfaces: z.array(z.enum(['workspace_web', 'extension'])),
    toolFields: z.array(adminV2ToolFieldSchema),
    modelUsages: z.array(
      z
        .object({
          id: z.string().min(1),
          role: z.enum(['main', 'branch', 'fallback', 'sequence', 'guard']),
          modelId: z.string().min(1),
          providerCostPolicyOverride:
            adminV2ProviderCostPolicySchema.optional(),
        })
        .strict()
    ),
    parameterMappings: z.array(parameterMappingSchema),
    executionPlan: z
      .object({
        nodes: z.array(executionNodeSchema),
      })
      .strict(),
    workspaceView: z
      .object({
        surfaceMode: z.enum(['canvas', 'conversation']),
        layoutVariant: z.enum([
          'classic',
          'primary-with-reference',
          'composer',
        ]),
        showcase: z
          .object({
            template: z.enum(['canvas-stage', 'prompt-demo']),
            compareEnabled: z.boolean(),
          })
          .strict()
          .nullable()
          .optional(),
        inputLabels: createAdminV2WorkspaceInputLabelsSchema(
          adminV2LocalizedTextSchema
        ).optional(),
        actionBindings: z
          .array(adminV2WorkspaceActionSlotBindingSchema)
          .optional(),
        toolbar: adminV2WorkspaceToolbarSchema.optional(),
        repeatGeneration: z
          .object({
            maxJobs: z.number().int().min(2).max(4),
          })
          .strict()
          .optional(),
        imageInputConstraintRules: z
          .array(workspaceImageInputConstraintRuleSchema)
          .optional(),
        parameterCalculators:
          adminV2ParameterCalculatorInstancesSchema.optional(),
        templatePicker: adminV2WorkspaceTemplatePickerDraftSchema.optional(),
        routeTemplatePicker:
          adminV2WorkspaceRouteTemplatePickerDraftSchema.optional(),
        output: z
          .object({
            outputType: toolOutputTypeSchema.optional(),
            outputNameSuffix: z.string().min(1).optional(),
            primaryImageSlotId: z.string().min(1).optional(),
            transparentBackground: z.boolean(),
          })
          .strict(),
      })
      .strict(),
    userCreditPolicy: z
      .object({
        paidAccessDiscount: adminV2PaidAccessDiscountSchema.optional(),
        terms: z.array(adminV2CreditTermSchema),
      })
      .strict(),
    accessPolicy: z
      .object({
        tool: accessPolicyRuleSchema,
        branches: z.array(
          z
            .object({
              nodeId: z.string().min(1),
              policy: accessPolicyRuleSchema,
            })
            .strict()
        ),
      })
      .strict(),
    translationMetadata: adminV2TranslationMetadataSchema.optional(),
  })
  .strict()
  .superRefine((draft, context) => {
    assertAdminV2WorkspaceActionBindings(
      draft.workspaceView.actionBindings ?? [],
      context,
      ['workspaceView', 'actionBindings']
    );
    const fieldIds = validateAdminV2ToolFieldList(draft.toolFields, context);
    const fieldsById = new Map(
      draft.toolFields.map((field) => [field.id, field])
    );
    const workspacePublishesToWeb =
      draft.workspaceEnabled &&
      draft.availableSurfaces.includes('workspace_web');
    const extensionPublishes =
      draft.workspaceEnabled && draft.availableSurfaces.includes('extension');
    const workspaceConsumedFieldIds = new Set<string>();

    const modelUsageIds = new Set(draft.modelUsages.map((usage) => usage.id));
    (draft.workspaceView.imageInputConstraintRules ?? []).forEach(
      (rule, ruleIndex) => {
        if (!fieldIds.has(rule.activeWhen.fieldId)) {
          context.addIssue({
            code: 'custom',
            message:
              'Workspace image input constraint rule references an unknown Tool field.',
            path: [
              'workspaceView',
              'imageInputConstraintRules',
              ruleIndex,
              'activeWhen',
              'fieldId',
            ],
          });
        }
        rule.modelUsageIds.forEach((usageId, usageIndex) => {
          if (modelUsageIds.has(usageId)) return;
          context.addIssue({
            code: 'custom',
            message:
              'Workspace image input constraint rule references an unknown Model usage.',
            path: [
              'workspaceView',
              'imageInputConstraintRules',
              ruleIndex,
              'modelUsageIds',
              usageIndex,
            ],
          });
        });
      }
    );

    (draft.workspaceView.parameterCalculators ?? []).forEach(
      (calculator, calculatorIndex) => {
        for (const issue of validateAdminV2ParameterCalculatorInstance({
          instance: calculator,
          toolFields: draft.toolFields,
        })) {
          context.addIssue({
            code: 'custom',
            message: issue.message,
            path: [
              'workspaceView',
              'parameterCalculators',
              calculatorIndex,
              ...issue.path,
            ],
          });
        }
      }
    );

    const templatePicker = draft.workspaceView.templatePicker;
    const routeTemplatePicker = draft.workspaceView.routeTemplatePicker;
    if (templatePicker && routeTemplatePicker) {
      context.addIssue({
        code: 'custom',
        message:
          'Workspace Prompt combination and route template pickers cannot be enabled together.',
        path: ['workspaceView', 'routeTemplatePicker'],
      });
    }
    if (templatePicker) {
      const groupIds = new Set<string>();
      const selectionFieldIds = new Set<string>();
      const customImageFieldIds = new Set<string>();

      templatePicker.groups.forEach((group, groupIndex) => {
        const groupPath = [
          'workspaceView',
          'templatePicker',
          'groups',
          groupIndex,
        ];
        if (groupIds.has(group.id)) {
          context.addIssue({
            code: 'custom',
            message: 'Workspace template picker group ids must be unique.',
            path: [...groupPath, 'id'],
          });
        }
        groupIds.add(group.id);

        if (
          selectionFieldIds.has(group.selectionFieldId) ||
          customImageFieldIds.has(group.selectionFieldId)
        ) {
          context.addIssue({
            code: 'custom',
            message:
              'Workspace template picker selection fields must be unique.',
            path: [...groupPath, 'selectionFieldId'],
          });
        }
        selectionFieldIds.add(group.selectionFieldId);

        const selectionField = fieldsById.get(group.selectionFieldId);
        if (
          !selectionField ||
          selectionField.control !== 'select' ||
          selectionField.inputRole !== 'parameter' ||
          selectionField.required ||
          selectionField.defaultValue !== undefined ||
          !isAdminV2UserFacingToolField(selectionField)
        ) {
          context.addIssue({
            code: 'custom',
            message:
              'Workspace template picker selectionFieldId must reference a visible, optional select parameter field without a default value.',
            path: [...groupPath, 'selectionFieldId'],
          });
        }

        const templatesByValue = new Map<string, number>();
        group.templates.forEach((template, templateIndex) => {
          if (templatesByValue.has(template.value)) {
            context.addIssue({
              code: 'custom',
              message:
                'Workspace template picker template values must be unique within a group.',
              path: [...groupPath, 'templates', templateIndex, 'value'],
            });
          }
          templatesByValue.set(template.value, templateIndex);
        });

        const options = selectionField?.options ?? [];
        options.forEach((option, optionIndex) => {
          if (typeof option.value !== 'string') {
            context.addIssue({
              code: 'custom',
              message:
                'Workspace template picker select options must use string values.',
              path: [
                'toolFields',
                draft.toolFields.indexOf(selectionField!),
                'options',
                optionIndex,
                'value',
              ],
            });
            return;
          }
          if (!option.hidden && !templatesByValue.has(option.value)) {
            context.addIssue({
              code: 'custom',
              message:
                'Every visible Workspace template picker option must have one template config.',
              path: [...groupPath, 'templates'],
            });
          }
        });
        group.templates.forEach((template, templateIndex) => {
          if (
            !options.some(
              (option) =>
                typeof option.value === 'string' &&
                option.value === template.value
            )
          ) {
            context.addIssue({
              code: 'custom',
              message:
                'Workspace template picker template config references an unknown select option.',
              path: [...groupPath, 'templates', templateIndex, 'value'],
            });
          }
        });

        if (
          group.required &&
          !options.some(
            (option) =>
              typeof option.value === 'string' &&
              !option.hidden &&
              !option.disabled &&
              templatesByValue.has(option.value)
          )
        ) {
          context.addIssue({
            code: 'custom',
            message:
              'A required Workspace template picker group must have at least one visible, enabled official template.',
            path: [...groupPath, 'required'],
          });
        }

        const hasCustomImageField = group.customImageFieldId !== undefined;
        const hasCustomPrompt = group.customPrompt !== undefined;
        if (hasCustomImageField !== hasCustomPrompt) {
          context.addIssue({
            code: 'custom',
            message:
              'Workspace template picker customImageFieldId and customPrompt must be configured together.',
            path: [...groupPath, 'customImageFieldId'],
          });
        }
        if (group.customImageFieldId) {
          if (
            customImageFieldIds.has(group.customImageFieldId) ||
            selectionFieldIds.has(group.customImageFieldId)
          ) {
            context.addIssue({
              code: 'custom',
              message:
                'Workspace template picker custom image fields must be unique.',
              path: [...groupPath, 'customImageFieldId'],
            });
          }
          customImageFieldIds.add(group.customImageFieldId);
          const customImageField = fieldsById.get(group.customImageFieldId);
          if (
            !customImageField ||
            customImageField.control !== 'image_file' ||
            customImageField.inputRole !== 'referenceImage' ||
            customImageField.required ||
            customImageField.allowBatch ||
            customImageField.imageConstraints?.maxCount !== 1 ||
            !isAdminV2UserFacingToolField(customImageField)
          ) {
            context.addIssue({
              code: 'custom',
              message:
                'Workspace template picker customImageFieldId must reference a visible, optional, non-batch reference image field with maxCount 1.',
              path: [...groupPath, 'customImageFieldId'],
            });
          }
        }
      });
    }

    if (routeTemplatePicker) {
      const pickerPath = ['workspaceView', 'routeTemplatePicker'];
      const selectionField = fieldsById.get(
        routeTemplatePicker.selectionFieldId
      );
      if (
        !selectionField ||
        selectionField.control !== 'select' ||
        selectionField.inputRole !== 'parameter' ||
        selectionField.required ||
        selectionField.defaultValue !== undefined ||
        !isAdminV2UserFacingToolField(selectionField)
      ) {
        context.addIssue({
          code: 'custom',
          message:
            'Workspace route template picker selectionFieldId must reference a visible, optional select parameter field without a default value.',
          path: [...pickerPath, 'selectionFieldId'],
        });
      }

      const templatesByValue = new Map<string, number>();
      routeTemplatePicker.templates.forEach((template, templateIndex) => {
        if (templatesByValue.has(template.value)) {
          context.addIssue({
            code: 'custom',
            message: 'Workspace route template values must be unique.',
            path: [...pickerPath, 'templates', templateIndex, 'value'],
          });
        }
        templatesByValue.set(template.value, templateIndex);
      });

      const options = selectionField?.options ?? [];
      options.forEach((option, optionIndex) => {
        if (typeof option.value !== 'string') {
          context.addIssue({
            code: 'custom',
            message:
              'Workspace route template select options must use string values.',
            path: [
              'toolFields',
              draft.toolFields.indexOf(selectionField!),
              'options',
              optionIndex,
              'value',
            ],
          });
          return;
        }
        if (!option.hidden && !templatesByValue.has(option.value)) {
          context.addIssue({
            code: 'custom',
            message:
              'Every visible Workspace route template option must have one template config.',
            path: [...pickerPath, 'templates'],
          });
        }
      });
      routeTemplatePicker.templates.forEach((template, templateIndex) => {
        if (
          !options.some(
            (option) =>
              typeof option.value === 'string' &&
              option.value === template.value
          )
        ) {
          context.addIssue({
            code: 'custom',
            message:
              'Workspace route template references an unknown select option.',
            path: [...pickerPath, 'templates', templateIndex, 'value'],
          });
        }
      });
      if (
        !options.some(
          (option) =>
            typeof option.value === 'string' &&
            !option.hidden &&
            !option.disabled &&
            templatesByValue.has(option.value)
        )
      ) {
        context.addIssue({
          code: 'custom',
          message:
            'Workspace route template picker must have at least one visible, enabled template.',
          path: [...pickerPath, 'templates'],
        });
      }
    }

    const validateClientProvidedMetaReference = (params: {
      fieldId: string;
      metaPath: string[];
      path: Array<string | number>;
    }) => {
      if (
        !params.metaPath[0] ||
        !CLIENT_IMAGE_META_DIRECT_TRUST_FIELDS.includes(
          params
            .metaPath[0] as (typeof CLIENT_IMAGE_META_DIRECT_TRUST_FIELDS)[number]
        )
      ) {
        context.addIssue({
          code: 'custom',
          message:
            'Client-provided image metadata may only read whitelisted width, height, format, or transparency fields.',
          path: [...params.path, 'metaPath'],
        });
        return;
      }

      const field = fieldsById.get(params.fieldId);
      const requestedField = params.metaPath[0];
      if (
        !field?.clientImageMetaRequirements?.fields.some(
          (fieldName) =>
            isClientImageMetaDirectTrustField(fieldName) &&
            fieldName === requestedField
        )
      ) {
        context.addIssue({
          code: 'custom',
          message:
            'Client-provided image metadata must be declared by the image field client metadata requirements.',
          path: [...params.path, 'fieldId'],
        });
      }
    };

    draft.toolFields.forEach((field, index) => {
      const capability = getAdminV2ToolFieldControlCapability(field.control);
      if (field.imageConstraints) {
        const requiredMetaFields = getRequiredAdminV2ClientImageMetaFields(
          field.imageConstraints
        );
        const declaredMetaFields = new Set(
          field.clientImageMetaRequirements?.fields ?? []
        );
        const missingMetaFields = requiredMetaFields.filter(
          (fieldName) => !declaredMetaFields.has(fieldName)
        );
        if (missingMetaFields.length > 0) {
          context.addIssue({
            code: 'custom',
            message: `Image constraints require client image metadata fields: ${missingMetaFields.join(', ')}.`,
            path: ['toolFields', index, 'clientImageMetaRequirements'],
          });
        }
      }
      if (workspacePublishesToWeb) {
        const consumedByWorkspaceRuntime =
          capability.consumedByWorkspaceParamList ||
          capability.consumedByWorkspaceUploadArea;
        if (consumedByWorkspaceRuntime) {
          workspaceConsumedFieldIds.add(field.id);
        }
        if (!consumedByWorkspaceRuntime && field.userVisible) {
          context.addIssue({
            code: 'custom',
            message:
              'Workspace-visible Tool fields must use controls consumed by Workspace runtime.',
            path: ['toolFields', index, 'control'],
          });
        }
      }
      if (
        extensionPublishes &&
        field.userVisible &&
        !field.adminOnly &&
        !capability.extensionSupported
      ) {
        context.addIssue({
          code: 'custom',
          message:
            'Extension-visible Tool fields must use controls supported by Extension catalog.',
          path: ['toolFields', index, 'control'],
        });
      }
    });
    const usageIds = new Set<string>();
    draft.modelUsages.forEach((usage, index) => {
      if (usageIds.has(usage.id)) {
        context.addIssue({
          code: 'custom',
          message: 'Model usage ids must be unique.',
          path: ['modelUsages', index, 'id'],
        });
      }
      usageIds.add(usage.id);
    });

    const executionNodeIds = new Set(
      draft.executionPlan.nodes.map((node) => node.id)
    );
    draft.userCreditPolicy.terms.forEach((term, termIndex) => {
      term.factors.forEach((factor, factorIndex) => {
        if (
          (factor.kind === 'toolField' || factor.kind === 'inputCount') &&
          !fieldIds.has(factor.fieldId)
        ) {
          context.addIssue({
            code: 'custom',
            message: 'Credit factor references an unknown Tool field.',
            path: [
              'userCreditPolicy',
              'terms',
              termIndex,
              'factors',
              factorIndex,
              'fieldId',
            ],
          });
        }
        if (factor.kind === 'conditional') {
          factor.conditions.forEach((condition, conditionIndex) => {
            if (!fieldIds.has(condition.fieldId)) {
              context.addIssue({
                code: 'custom',
                message: 'Credit condition references an unknown Tool field.',
                path: [
                  'userCreditPolicy',
                  'terms',
                  termIndex,
                  'factors',
                  factorIndex,
                  'conditions',
                  conditionIndex,
                  'fieldId',
                ],
              });
              return;
            }
            if (condition.source === 'clientImageMeta') {
              validateClientProvidedMetaReference({
                fieldId: condition.fieldId,
                metaPath: condition.metaPath,
                path: [
                  'userCreditPolicy',
                  'terms',
                  termIndex,
                  'factors',
                  factorIndex,
                  'conditions',
                  conditionIndex,
                ],
              });
            }
          });
        }
        if (factor.kind === 'mediaMeta' && !fieldIds.has(factor.fieldId)) {
          context.addIssue({
            code: 'custom',
            message:
              'Credit mediaMeta factor references an unknown Tool field.',
            path: [
              'userCreditPolicy',
              'terms',
              termIndex,
              'factors',
              factorIndex,
              'fieldId',
            ],
          });
        }
        if (
          factor.kind === 'mediaMeta' &&
          factor.metaSource === 'clientProvidedMeta'
        ) {
          validateClientProvidedMetaReference({
            fieldId: factor.fieldId,
            metaPath: factor.metaPath,
            path: [
              'userCreditPolicy',
              'terms',
              termIndex,
              'factors',
              factorIndex,
            ],
          });
        }
        if (
          factor.kind === 'nodeExecuted' &&
          !executionNodeIds.has(factor.nodeId)
        ) {
          context.addIssue({
            code: 'custom',
            message:
              'Credit nodeExecuted factor references an unknown execution node.',
            path: [
              'userCreditPolicy',
              'terms',
              termIndex,
              'factors',
              factorIndex,
              'nodeId',
            ],
          });
        }
        if (factor.kind === 'executionBranch') {
          factor.branches.forEach((branch, branchIndex) => {
            if (!modelUsageIds.has(branch.modelUsageId)) {
              context.addIssue({
                code: 'custom',
                message:
                  'Credit executionBranch references an unknown model usage.',
                path: [
                  'userCreditPolicy',
                  'terms',
                  termIndex,
                  'factors',
                  factorIndex,
                  'branches',
                  branchIndex,
                  'modelUsageId',
                ],
              });
            }
          });
        }
        if (
          factor.kind === 'registeredCalculator' &&
          !isAdminV2RegisteredCreditCalculatorKey(factor.key)
        ) {
          context.addIssue({
            code: 'custom',
            message: 'Credit calculator must be registered in code.',
            path: [
              'userCreditPolicy',
              'terms',
              termIndex,
              'factors',
              factorIndex,
              'key',
            ],
          });
        }
      });
    });
    const getMappingTargetParameterIds = (
      mapping: (typeof draft.parameterMappings)[number]
    ) =>
      mapping.targetBindings
        ? mapping.targetBindings.map((binding) => binding.targetParameterId)
        : mapping.targetParameterId
          ? [mapping.targetParameterId]
          : [];

    draft.parameterMappings.forEach((mapping, index) => {
      if (!usageIds.has(mapping.modelUsageId)) {
        context.addIssue({
          code: 'custom',
          message: 'Parameter mapping references an unknown model usage.',
          path: ['parameterMappings', index, 'modelUsageId'],
        });
      }
      if (
        mapping.source.kind === 'toolField' &&
        !fieldIds.has(mapping.source.fieldId)
      ) {
        context.addIssue({
          code: 'custom',
          message: 'Parameter mapping references an unknown Tool field.',
          path: ['parameterMappings', index, 'source', 'fieldId'],
        });
      }
      if (mapping.source.kind === 'toolField' && workspacePublishesToWeb) {
        const sourceField = fieldsById.get(mapping.source.fieldId);
        if (sourceField && !workspaceConsumedFieldIds.has(sourceField.id)) {
          context.addIssue({
            code: 'custom',
            message:
              'Parameter mapping source Tool field is not consumed by Workspace runtime.',
            path: ['parameterMappings', index, 'source', 'fieldId'],
          });
        }
      }
      if (
        mapping.source.kind === 'computedValue' &&
        !isAdminV2ComputedValueKey(mapping.source.key)
      ) {
        context.addIssue({
          code: 'custom',
          message: 'Computed value keys must be registered in code.',
          path: ['parameterMappings', index, 'source', 'key'],
        });
      }
      if (
        mapping.source.kind === 'computedResolver' &&
        !isAdminV2ComputedResolverKey(mapping.source.key)
      ) {
        context.addIssue({
          code: 'custom',
          message: 'Computed resolver keys must be registered in code.',
          path: ['parameterMappings', index, 'source', 'key'],
        });
      }
      if (
        mapping.source.kind === 'templateResolver' &&
        !isAdminV2TemplateResolverKey(mapping.source.key)
      ) {
        context.addIssue({
          code: 'custom',
          message: 'Template resolver keys must be registered in code.',
          path: ['parameterMappings', index, 'source', 'key'],
        });
      }
      if (mapping.source.kind === 'mediaMetaResolver') {
        if (!fieldIds.has(mapping.source.fieldId)) {
          context.addIssue({
            code: 'custom',
            message:
              'Media metadata resolver references an unknown Tool field.',
            path: ['parameterMappings', index, 'source', 'fieldId'],
          });
        }
        if (!isAdminV2MediaMetaResolverKey(mapping.source.key)) {
          context.addIssue({
            code: 'custom',
            message: 'Media metadata resolver keys must be registered in code.',
            path: ['parameterMappings', index, 'source', 'key'],
          });
        }
        if (mapping.source.metaSource === 'clientProvidedMeta') {
          validateClientProvidedMetaReference({
            fieldId: mapping.source.fieldId,
            metaPath: mapping.source.metaPath,
            path: ['parameterMappings', index, 'source'],
          });
        }
      }
      const previousTargets = new Set(
        draft.parameterMappings
          .slice(0, index)
          .flatMap((previous) =>
            getMappingTargetParameterIds(previous).map(
              (targetParameterId) =>
                `${previous.modelUsageId}:${targetParameterId}`
            )
          )
      );
      getMappingTargetParameterIds(mapping).forEach(
        (targetParameterId, targetIndex) => {
          const mappingKey = `${mapping.modelUsageId}:${targetParameterId}`;
          if (previousTargets.has(mappingKey)) {
            context.addIssue({
              code: 'custom',
              message:
                'Tool parameter mappings must be unique by model usage and target parameter.',
              path: mapping.targetBindings
                ? [
                    'parameterMappings',
                    index,
                    'targetBindings',
                    targetIndex,
                    'targetParameterId',
                  ]
                : ['parameterMappings', index, 'targetParameterId'],
            });
          }
        }
      );
    });

    const validateConditionSources = (
      condition: AdminV2ExecutionConditionInput,
      path: Array<string | number>
    ) => {
      const sourceCount = [
        condition.fieldId,
        condition.mediaFieldId,
        condition.previousOutput,
        condition.providerErrorCode,
        condition.registeredCondition,
        condition.and,
        condition.or,
        condition.not,
      ].filter((value) => value !== undefined).length;
      if (sourceCount !== 1) {
        context.addIssue({
          code: 'custom',
          message: 'branch nodes must use exactly one condition source.',
          path,
        });
      }
      if (
        condition.mediaFieldId &&
        (!condition.mediaMetaSource || !condition.mediaMetaPath)
      ) {
        context.addIssue({
          code: 'custom',
          message:
            'Media metadata conditions must include a meta source and meta path.',
          path,
        });
      }
      if (
        condition.mediaFieldId &&
        condition.mediaMetaSource === 'clientProvidedMeta' &&
        condition.mediaMetaPath
      ) {
        validateClientProvidedMetaReference({
          fieldId: condition.mediaFieldId,
          metaPath: condition.mediaMetaPath,
          path,
        });
      }
      if (
        !condition.mediaFieldId &&
        (condition.mediaMetaSource || condition.mediaMetaPath)
      ) {
        context.addIssue({
          code: 'custom',
          message:
            'Media metadata condition source/path require a media field id.',
          path,
        });
      }
      if (condition.fieldId && !fieldIds.has(condition.fieldId)) {
        context.addIssue({
          code: 'custom',
          message: 'branch node references an unknown Tool field.',
          path: [...path, 'fieldId'],
        });
      }
      if (condition.mediaFieldId && !fieldIds.has(condition.mediaFieldId)) {
        context.addIssue({
          code: 'custom',
          message: 'branch media condition references an unknown Tool field.',
          path: [...path, 'mediaFieldId'],
        });
      }
      if (
        condition.registeredCondition &&
        !isAdminV2RegisteredConditionKey(condition.registeredCondition)
      ) {
        context.addIssue({
          code: 'custom',
          message: 'Execution condition keys must be registered in code.',
          path: [...path, 'registeredCondition'],
        });
      }
      condition.and?.forEach((child, index) =>
        validateConditionSources(child, [...path, 'and', index])
      );
      condition.or?.forEach((child, index) =>
        validateConditionSources(child, [...path, 'or', index])
      );
      if (condition.not) {
        validateConditionSources(condition.not, [...path, 'not']);
      }
    };

    draft.executionPlan.nodes.forEach((node, index) => {
      if (
        node.nodeType === 'runModel' &&
        !usageIds.has(node.modelUsageId ?? '')
      ) {
        context.addIssue({
          code: 'custom',
          message: 'runModel node references an unknown model usage.',
          path: ['executionPlan', 'nodes', index, 'modelUsageId'],
        });
      }
      if (node.nodeType === 'branch') {
        if (!node.condition) {
          context.addIssue({
            code: 'custom',
            message: 'branch nodes must include a condition.',
            path: ['executionPlan', 'nodes', index, 'condition'],
          });
        } else {
          validateConditionSources(node.condition, [
            'executionPlan',
            'nodes',
            index,
            'condition',
          ]);
          if ((node.condition.elseChildren ?? []).length === 0) {
            context.addIssue({
              code: 'custom',
              message: 'branch nodes must include else child steps.',
              path: [
                'executionPlan',
                'nodes',
                index,
                'condition',
                'elseChildren',
              ],
            });
          }
        }
      } else if (node.condition) {
        context.addIssue({
          code: 'custom',
          message: 'Only branch nodes can include a condition.',
          path: ['executionPlan', 'nodes', index, 'condition'],
        });
      }
      if (
        (node.nodeType === 'sequence' ||
          node.nodeType === 'branch' ||
          node.nodeType === 'fallback' ||
          node.nodeType === 'guard') &&
        node.children.length === 0
      ) {
        context.addIssue({
          code: 'custom',
          message: `${node.nodeType} nodes must include at least one child step.`,
          path: ['executionPlan', 'nodes', index, 'children'],
        });
      }
      node.preflightValidators?.forEach((validator, validatorIndex) => {
        if (!isAdminV2PreflightValidatorKey(validator.key)) {
          context.addIssue({
            code: 'custom',
            message: 'Preflight validator keys must be registered in code.',
            path: [
              'executionPlan',
              'nodes',
              index,
              'preflightValidators',
              validatorIndex,
              'key',
            ],
          });
        }
      });
      node.mediaMetaTransformers?.forEach((transformer, transformerIndex) => {
        if (!isAdminV2MediaMetaTransformerKey(transformer.key)) {
          context.addIssue({
            code: 'custom',
            message:
              'Media metadata transformer keys must be registered in code.',
            path: [
              'executionPlan',
              'nodes',
              index,
              'mediaMetaTransformers',
              transformerIndex,
              'key',
            ],
          });
        }
      });
    });
    draft.accessPolicy.branches.forEach((branchPolicy, index) => {
      if (!executionNodeIds.has(branchPolicy.nodeId)) {
        context.addIssue({
          code: 'custom',
          message: 'Access branch policy references an unknown execution node.',
          path: ['accessPolicy', 'branches', index, 'nodeId'],
        });
      }
    });
  });

type AdminV2ParsedToolDraft = z.infer<typeof adminV2ToolDraftSchema>;
type AdminV2ParsedToolField = AdminV2ParsedToolDraft['toolFields'][number];
type AdminV2ParsedImageConstraints = NonNullable<
  AdminV2ParsedToolField['imageConstraints']
>;
type AdminV2ParsedClientImageMetaRequirements = NonNullable<
  AdminV2ParsedToolField['clientImageMetaRequirements']
>;
type AdminV2ParsedModelUsage = AdminV2ParsedToolDraft['modelUsages'][number];
type AdminV2ParsedToolFieldOption = AdminV2ParsedToolField['options'][number];

export type AdminV2ToolDraft = Omit<
  AdminV2ParsedToolDraft,
  'toolFields' | 'modelUsages' | 'workspaceView'
> & {
  workspaceView?: AdminV2ParsedToolDraft['workspaceView'];
  toolFields: Array<
    Omit<
      AdminV2ParsedToolField,
      | 'allowBatch'
      | 'inputRole'
      | 'prefillable'
      | 'adminOnly'
      | 'userVisible'
      | 'imageConstraints'
      | 'clientImageMetaRequirements'
      | 'options'
    > &
      Partial<
        Pick<
          AdminV2ParsedToolField,
          | 'allowBatch'
          | 'inputRole'
          | 'prefillable'
          | 'adminOnly'
          | 'userVisible'
        >
      > & {
        imageConstraints?: Partial<AdminV2ParsedImageConstraints>;
        clientImageMetaRequirements?: AdminV2ParsedClientImageMetaRequirements;
        options?: Array<
          Omit<AdminV2ParsedToolFieldOption, 'hidden' | 'disabled'> &
            Partial<Pick<AdminV2ParsedToolFieldOption, 'hidden' | 'disabled'>>
        >;
      }
  >;
  modelUsages: Array<
    Omit<AdminV2ParsedModelUsage, 'role'> &
      Partial<Pick<AdminV2ParsedModelUsage, 'role'>>
  >;
};

export function parseAdminV2ToolDraft(input: unknown) {
  return adminV2ToolDraftSchema.parse(input);
}

const adminV2NewToolDraftSchema = z
  .object({
    workspaceView: z
      .object({
        showcase: z
          .never({
            error: 'workspaceView.showcase is not supported for new Tools.',
          })
          .optional(),
      })
      .passthrough(),
  })
  .passthrough();

export function parseAdminV2NewToolDraft(input: unknown) {
  adminV2NewToolDraftSchema.parse(input);
  return adminV2ToolDraftSchema.parse(input);
}

export function omitAdminV2ToolShowcase(draft: AdminV2ToolDraft) {
  if (!draft.workspaceView) {
    return structuredClone(draft);
  }
  const { showcase: _legacyShowcase, ...workspaceView } = draft.workspaceView;
  return structuredClone({ ...draft, workspaceView });
}
